import * as React from "react"
import { toast as sonnerToast } from "sonner"
import type { ExternalToast } from "sonner"
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2, X } from "lucide-react"
import "./toastAnimation.css"
import { useTranslation } from "../../i18n/LanguageContext"

type VariantType = "success" | "error" | "warning" | "info" | "pending" | "default"

/**
 * Which of our toast cards are on screen, so a card can offer "Clear all" when
 * it is not alone. Each card registers while it is mounted: counting created or
 * closed toasts instead goes wrong, because sonner's `useSonner()` only sees
 * toasts created after it mounted, and the Toaster drops a toast published in
 * the gap while it re-subscribes — created, never shown, never closed.
 */
const openToasts = new Set<string | number>()
const openListeners = new Set<() => void>()
function setOpen(id: string | number, open: boolean) {
  const had = openToasts.has(id)
  if (open === had) return
  if (open) openToasts.add(id)
  else openToasts.delete(id)
  refreshSnapshot()
  openListeners.forEach((l) => l())
}
function subscribeOpen(listener: () => void) {
  openListeners.add(listener)
  return () => { openListeners.delete(listener) }
}
/** Open count, and the newest open toast — the front card, the only one that offers "Clear all". */
let openSnapshot = { count: 0, newest: null as string | number | null }
function refreshSnapshot() {
  const ids = [...openToasts]
  openSnapshot = { count: ids.length, newest: ids.length ? ids[ids.length - 1] : null }
}
const getOpenSnapshot = () => openSnapshot

function dismissAll() {
  dismiss()
}

/**
 * Where a meeting screen redirects every toast call while it is mounted.
 *
 * `toast.info`/`.success`/... and `dismiss`/`dismissAll` are called from
 * roughly sixty places across the app, on the dashboard and in a meeting
 * alike. Rather than teach all of them about meetings, a meeting mounts its
 * own notice stack and points this one module-level switch at it; every
 * `toast.*` call checks it first and, while it is set, hands the notice
 * straight to the sink instead of ever reaching sonner. Unmounting the
 * meeting clears it, and the dashboard's own toasts (which never set it)
 * are untouched throughout.
 */
export interface ToastSinkNotice {
  id: string | number
  title: string
  description?: string
  variant: VariantType
  duration: number
  action?: ToastAction
}
export interface ToastSink {
  push: (notice: ToastSinkNotice) => void
  /** Undefined clears every notice the sink is holding, same as sonner's `dismissAll`. */
  dismiss: (id?: string | number) => void
}
let toastSink: ToastSink | null = null
export function setToastSink(sink: ToastSink | null) {
  toastSink = sink
}

const variantConfig: Record<
  VariantType,
  { icon: React.ElementType; iconClass: string; borderClass: string; progressClass: string }
> = {
  success: {
    icon: CheckCircle2,
    iconClass: "text-emerald-500",
    borderClass: "border-l-emerald-500",
    progressClass: "bg-emerald-500",
  },
  error: {
    icon: XCircle,
    iconClass: "text-red-500",
    borderClass: "border-l-red-500",
    progressClass: "bg-red-500",
  },
  warning: {
    icon: AlertTriangle,
    iconClass: "text-amber-500",
    borderClass: "border-l-amber-500",
    progressClass: "bg-amber-500",
  },
  info: {
    icon: Info,
    iconClass: "text-blue-500",
    borderClass: "border-l-blue-500",
    progressClass: "bg-blue-500",
  },
  pending: {
    icon: Loader2,
    iconClass: "text-violet-500 animate-spin",
    borderClass: "border-l-violet-500",
    progressClass: "bg-violet-500",
  },
  default: {
    icon: Info,
    iconClass: "text-muted-foreground",
    borderClass: "border-l-border",
    progressClass: "bg-muted-foreground",
  },
}

/** An inline button on the toast. Sonner's own `action` is not used — this
 *  card renders its own layout, so it would never appear. */
export interface ToastAction {
  label: string
  onClick: () => void
}

interface ToastOptions extends Omit<ExternalToast, "className" | "action"> {
  title: string
  description?: string
  variant?: VariantType
  duration?: number
  action?: ToastAction
}

function ToastCard({
  title,
  description,
  variant = "default",
  toastId,
  duration = 4000,
  action,
}: {
  title: string
  description?: string
  variant?: VariantType
  toastId: string | number
  duration?: number
  action?: ToastAction
}) {
  const { icon: Icon, iconClass, borderClass, progressClass } =
    variantConfig[variant] ?? variantConfig.default
  const t = useTranslation()
  // Several notices at once (a busy meeting) could only be closed one by one.
  // Offered once, on the newest card — not repeated down an expanded stack.
  const open = React.useSyncExternalStore(subscribeOpen, getOpenSnapshot)
  React.useEffect(() => {
    setOpen(toastId, true)
    return () => setOpen(toastId, false)
  }, [toastId])
  const showClearAll = open.count > 1 && open.newest === toastId

  return (
    <div
      className={`relative flex items-start gap-3 bg-card border border-border border-l-[3px] ${borderClass} rounded-xl shadow-lg px-4 py-3.5 w-full pointer-events-auto overflow-hidden`}
    >
      <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${iconClass}`} />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground leading-tight">{title}</p>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{description}</p>
        )}
        {action && (
          <button
            onClick={() => {
              action.onClick()
              sonnerToast.dismiss(toastId)
            }}
            className="mt-2 text-xs font-semibold text-primary hover:underline"
          >
            {action.label}
          </button>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-1 mt-0.5">
        {showClearAll && (
          <button
            onClick={dismissAll}
            className="px-1.5 py-0.5 rounded-md text-[11px] font-semibold text-primary hover:bg-muted transition-colors whitespace-nowrap"
          >
            {t("panels.clearAll")} ({open.count})
          </button>
        )}
        <button
          onClick={() => sonnerToast.dismiss(toastId)}
          aria-label={t("common.close") || "Close"}
          className="p-0.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Progress track */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-border/40">
        {/* Shrinking fill — animates from full width to zero */}
        <div
          className={`h-full w-full origin-right ${progressClass}`}
          style={{
            animation: `toast-progress ${duration}ms linear forwards`,
          }}
        />
      </div>
    </div>
  )
}

let nextToastId = 0
/** Created, not yet handed to sonner — see createToast. The latest call per id wins. */
const queuedToasts = new Map<string | number, object>()

/**
 * Published on the next tick, not now. Sonner's Toaster re-subscribes to its
 * feed every time its list changes, and a toast published while it is between
 * the two — several notices in the same React update, e.g. a lock, a knock and
 * a recording landing together — is dropped: created, never shown. Deferring
 * puts every publish after that commit. The id is fixed up front so callers
 * still get it back synchronously and can dismiss it.
 */
function createToast(options: ToastOptions) {
  const { title, description, variant = "default", duration = 4000, action, ...rest } = options
  const id = rest.id ?? `toast-${++nextToastId}`
  if (toastSink) {
    toastSink.push({ id, title, description, variant, duration, action })
    return id
  }
  const token = {}
  queuedToasts.set(id, token)
  setTimeout(() => {
    // Dismissed before it was shown, or replaced by a later call for this id.
    if (queuedToasts.get(id) !== token) return
    queuedToasts.delete(id)
    sonnerToast.custom(
      (toastId) => (
        <ToastCard
          title={title}
          description={description}
          variant={variant}
          toastId={toastId}
          duration={duration}
          action={action}
        />
      ),
      { duration, ...rest, id },
    )
  }, 0)
  return id
}

function dismiss(id?: string | number) {
  if (toastSink) {
    toastSink.dismiss(id)
    return id
  }
  if (id === undefined) queuedToasts.clear()
  else queuedToasts.delete(id)
  return sonnerToast.dismiss(id)
}

export const toast = Object.assign(
  (options: ToastOptions) => createToast(options),
  {
    success: (options: Omit<ToastOptions, "variant">) => createToast({ ...options, variant: "success" }),
    error:   (options: Omit<ToastOptions, "variant">) => createToast({ ...options, variant: "error" }),
    warning: (options: Omit<ToastOptions, "variant">) => createToast({ ...options, variant: "warning" }),
    info:    (options: Omit<ToastOptions, "variant">) => createToast({ ...options, variant: "info" }),
    pending: (options: Omit<ToastOptions, "variant">) => createToast({ ...options, variant: "pending" }),
    promise: sonnerToast.promise,
    dismiss,
  },
)

export type { VariantType }
